import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Download, Share, Plus, X } from "lucide-react";

const STORAGE_KEY = "allied_pod_install_prompt";
const SESSION_LOGIN_KEY = "allied_pod_login_ts";
const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7-day cooldown for the *auto* banner only
const BANNER_WINDOW_MS = 30 * 1000; // 30 seconds post-login

function isStandalone() {
    if (typeof window === "undefined") return false;
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
    if (window.navigator.standalone === true) return true; // iOS
    return false;
}
function isIOS() {
    const ua = window.navigator.userAgent || "";
    const isiPhoneOrIPad = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isiPadOS = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
    return isiPhoneOrIPad || isiPadOS;
}
function isSafari() {
    const ua = window.navigator.userAgent || "";
    return /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
}
function readState() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
}
function writeState(state) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
        /* ignore quota / private mode */
    }
}

// mode: 'hidden' | 'banner' | 'button'
export default function PWAInstallPrompt() {
    const location = useLocation();
    const isDashboard = location.pathname === "/dashboard";
    const [mode, setMode] = useState("hidden");
    const [deferred, setDeferred] = useState(null);
    const [installed, setInstalled] = useState(false);
    const bannerTimerRef = useRef(null);

    const ios = typeof window !== "undefined" && isIOS() && isSafari();

    // Capture beforeinstallprompt + appinstalled. The actual `beforeinstallprompt`
    // listener is set in src/index.js (so it never misses the once-per-page event
    // due to route-change unmounts). We just sync from there.
    useEffect(() => {
        if (typeof window === "undefined") return;
        if (isStandalone()) {
            setInstalled(true);
            return;
        }
        const state = readState();
        if (state.installed) {
            setInstalled(true);
            return;
        }

        // Re-hydrate from window if a previous mount already captured it
        if (window.__alliedDeferredBIP) {
            setDeferred(window.__alliedDeferredBIP);
        }

        const onBIPReady = () => {
            if (window.__alliedDeferredBIP) setDeferred(window.__alliedDeferredBIP);
        };
        const onInstalled = () => {
            setInstalled(true);
            setMode("hidden");
        };
        window.addEventListener("allied-bip-ready", onBIPReady);
        window.addEventListener("appinstalled", onInstalled);
        return () => {
            window.removeEventListener("allied-bip-ready", onBIPReady);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    // Decide what to show on the dashboard
    useEffect(() => {
        if (installed) {
            setMode("hidden");
            return;
        }
        if (!isDashboard) {
            setMode("hidden");
            return;
        }

        const params = new URLSearchParams(location.search);
        const force = params.get("install") === "1";
        const state = readState();
        const inCooldown = !!state.dismissedAt && Date.now() - state.dismissedAt < COOLDOWN_MS;

        if (force) {
            // Force-show banner: bypass cooldown, restart 30s window
            sessionStorage.setItem(SESSION_LOGIN_KEY, Date.now().toString());
            setMode("banner");
            return;
        }

        const startedRaw = sessionStorage.getItem(SESSION_LOGIN_KEY);
        if (!startedRaw) {
            // First time hitting dashboard this session = first 30s post-login
            sessionStorage.setItem(SESSION_LOGIN_KEY, Date.now().toString());
            setMode(inCooldown ? "button" : "banner");
            return;
        }

        const elapsed = Date.now() - parseInt(startedRaw, 10);
        if (elapsed < BANNER_WINDOW_MS && !inCooldown) {
            setMode("banner");
        } else {
            setMode("button");
        }
    }, [installed, isDashboard, location.pathname, location.search]);

    // Auto-collapse banner → button after the remaining 30s
    useEffect(() => {
        if (mode !== "banner") return;
        const startedRaw = sessionStorage.getItem(SESSION_LOGIN_KEY);
        const started = startedRaw ? parseInt(startedRaw, 10) : Date.now();
        const remaining = Math.max(0, BANNER_WINDOW_MS - (Date.now() - started));
        bannerTimerRef.current = setTimeout(() => setMode("button"), remaining);
        return () => clearTimeout(bannerTimerRef.current);
    }, [mode]);

    // Hide entirely on browsers that can't install (no deferred + not iOS Safari)
    const canInstall = ios || !!deferred;
    if (installed || !isDashboard || mode === "hidden" || !canInstall) return null;

    const dismissBanner = () => {
        writeState({ ...readState(), dismissedAt: Date.now() });
        setMode("button");
    };

    const triggerInstall = async () => {
        if (ios && !deferred) {
            // iOS path: just show the instructional banner (already in banner mode if user clicked button)
            setMode("banner");
            // Restart timer so it doesn't auto-collapse during reading
            sessionStorage.setItem(SESSION_LOGIN_KEY, Date.now().toString());
            return;
        }
        if (!deferred) return;
        try {
            deferred.prompt();
            const choice = await deferred.userChoice;
            if (choice && choice.outcome === "accepted") {
                writeState({ ...readState(), installed: true });
                setInstalled(true);
                setMode("hidden");
            } else {
                writeState({ ...readState(), dismissedAt: Date.now() });
                setMode("button");
            }
        } catch {
            writeState({ ...readState(), dismissedAt: Date.now() });
            setMode("button");
        } finally {
            setDeferred(null);
        }
    };

    if (mode === "button") {
        return (
            <button
                type="button"
                onClick={() => {
                    sessionStorage.setItem(SESSION_LOGIN_KEY, Date.now().toString());
                    setMode("banner");
                }}
                data-testid="pwa-install-button"
                aria-label="Install Allied Pod"
                className="fixed bottom-5 right-5 z-[60] inline-flex items-center gap-2 bg-brand hover:bg-brand-hover text-white rounded-full pl-4 pr-5 py-2.5 shadow-lg shadow-brand/25 hover:shadow-xl hover:shadow-brand/30 transition-all hover:-translate-y-0.5"
            >
                <Download className="h-4 w-4" strokeWidth={2} />
                <span className="text-sm font-medium tracking-tight">Install</span>
            </button>
        );
    }

    // banner mode
    return (
        <div
            data-testid="pwa-install-banner"
            className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-4 sm:px-6 sm:pb-6 pointer-events-none"
        >
            <div className="mx-auto max-w-md pointer-events-auto">
                <div className="relative bg-white border border-slate-200 shadow-2xl rounded-xl overflow-hidden">
                    <div className="absolute inset-x-0 top-0 h-1 bg-brand" />
                    <button
                        type="button"
                        onClick={dismissBanner}
                        data-testid="pwa-install-close-btn"
                        aria-label="Dismiss"
                        className="absolute top-3 right-3 text-slate-400 hover:text-slate-700 transition-colors"
                    >
                        <X className="h-4 w-4" strokeWidth={2} />
                    </button>
                    <div className="p-5 pt-6">
                        <div className="flex items-center gap-3">
                            <div className="h-11 w-11 bg-brand text-white grid place-items-center font-heading font-bold rounded-md shrink-0">
                                A
                            </div>
                            <div className="leading-tight">
                                <div className="font-heading text-base font-bold text-slate-900">Install Allied Pod</div>
                                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono">Faster booking. One tap.</div>
                            </div>
                        </div>

                        {ios && !deferred ? (
                            <div className="mt-4 text-sm text-slate-600 space-y-2">
                                <p>Add Allied Pod to your home screen for quick access:</p>
                                <ol className="space-y-1.5 pl-1">
                                    <li className="flex items-center gap-2">
                                        <span className="text-[10px] font-mono text-slate-500">1.</span>
                                        <span>Tap</span>
                                        <Share className="h-4 w-4 text-brand" strokeWidth={1.75} />
                                        <span>in the Safari toolbar.</span>
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <span className="text-[10px] font-mono text-slate-500">2.</span>
                                        <span>Choose</span>
                                        <span className="inline-flex items-center gap-1 font-medium text-slate-900">
                                            Add to Home Screen
                                            <Plus className="h-3.5 w-3.5" strokeWidth={2} />
                                        </span>
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <span className="text-[10px] font-mono text-slate-500">3.</span>
                                        <span>Tap <span className="font-medium text-slate-900">Add</span>.</span>
                                    </li>
                                </ol>
                                <div className="pt-2">
                                    <Button
                                        variant="outline"
                                        onClick={dismissBanner}
                                        data-testid="pwa-install-ios-dismiss-btn"
                                        className="w-full rounded-md"
                                    >
                                        Got it
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="mt-4 text-sm text-slate-600">
                                <p>Install the app for instant access, faster bookings, and home-screen launch.</p>
                                <div className="mt-4 flex gap-2">
                                    <Button
                                        variant="outline"
                                        onClick={dismissBanner}
                                        data-testid="pwa-install-later-btn"
                                        className="rounded-md flex-1"
                                    >
                                        Not now
                                    </Button>
                                    <Button
                                        onClick={triggerInstall}
                                        data-testid="pwa-install-confirm-btn"
                                        className="bg-brand hover:bg-brand-hover text-white rounded-md flex-1"
                                    >
                                        <Download className="h-4 w-4 mr-1.5" strokeWidth={1.75} />
                                        Install
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
